import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import { initAnalytics } from "@/lib/analytics";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingJoinCTA from "@/components/FloatingJoinCTA";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import StudentDashboard from "@/pages/StudentDashboard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import LiveRoom from "@/pages/LiveRoom";
import LiveList from "@/pages/LiveList";
import DemoRoom from "@/pages/DemoRoom";
import WebinarsList from "@/pages/WebinarsList";
import WebinarDetail from "@/pages/WebinarDetail";
import WebinarSeriesList from "@/pages/WebinarSeriesList";
import { CommunityPage, ChallengesPage } from "@/pages/CommunityChallenges";
import AuthCallback from "@/pages/AuthCallback";
import AICounselorChat from "@/components/AICounselorChat";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import LiveActivityPopup from "@/components/LiveActivityPopup";
import StickyCTABar from "@/components/StickyCTABar";
import FloatingCallButton from "@/components/FloatingCallButton";
import CityCoursePage from "@/pages/CityCoursePage";
import WallOfFame from "@/pages/WallOfFame";
import FacultyProfile from "@/pages/FacultyProfile";
import { useParams } from "react-router-dom";

// Known course slugs and city slugs — used to detect SEO city landing URLs
// like /courses/digital-marketing-master-indore vs /courses/<uuid>
const KNOWN_COURSE_SLUGS = ["digital-marketing-master", "meta-ads", "google-ads", "seo-specialist", "graphic-design", "video-editing"];
const KNOWN_CITY_SLUGS = ["indore", "bhopal", "ujjain", "dewas", "ratlam", "khargone", "gwalior", "jabalpur", "sagar", "mumbai", "delhi", "bengaluru", "hyderabad", "pune", "ahmedabad", "chennai", "kolkata", "jaipur", "lucknow"];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function CourseRouteDispatcher() {
  const { id } = useParams();
  if (id && !UUID_RE.test(id)) {
    const lower = id.toLowerCase();
    // Check if it's a slug-city pattern → city landing page
    for (const city of KNOWN_CITY_SLUGS) {
      if (lower.endsWith(`-${city}`)) {
        const courseSlug = lower.slice(0, -(city.length + 1));
        if (KNOWN_COURSE_SLUGS.includes(courseSlug)) {
          return <CityCoursePage />;
        }
      }
    }
    // Otherwise treat as a course slug (SEO-friendly URL like /courses/google-ads)
    // CourseDetail will fetch by slug via the same /api/courses/{id_or_slug} endpoint
  }
  return <CourseDetail />;
}


function Shell({ children, hideChrome }) {
  return (
    <div className="App grain min-h-screen relative">
      {!hideChrome && <StickyCTABar />}
      {!hideChrome && <Navbar />}
      <main className="relative z-10">{children}</main>
      {!hideChrome && <Footer />}
      {!hideChrome && <FloatingJoinCTA />}
      {!hideChrome && <FloatingCallButton />}
      {!hideChrome && <AICounselorChat />}
      {!hideChrome && <LiveActivityPopup />}
      {!hideChrome && <ExitIntentPopup />}
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

function App() {
  React.useEffect(() => { initAnalytics(); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <RouterContent />
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

function RouterContent() {
  // CRITICAL: Synchronous hash check for Google OAuth session_id — must run BEFORE other routes
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <Shell><AuthCallback /></Shell>;
  }
  return (
    <Routes>
      <Route path="/" element={<Shell><Landing /></Shell>} />
      <Route path="/courses" element={<Shell><Courses /></Shell>} />
      <Route path="/courses/:id" element={<Shell><CourseRouteDispatcher /></Shell>} />
      <Route path="/faculty/:slug" element={<Shell><FacultyProfile /></Shell>} />
      <Route path="/login" element={<Shell><Login /></Shell>} />
      <Route path="/register" element={<Shell><Register /></Shell>} />
      <Route path="/live" element={<Shell><LiveList /></Shell>} />
      <Route path="/demo/:classId" element={<DemoRoom />} />
      <Route path="/webinars" element={<Shell><WebinarsList /></Shell>} />
      <Route path="/webinar-series" element={<Shell><WebinarSeriesList /></Shell>} />
      <Route path="/webinar/:slug" element={<Shell><WebinarDetail /></Shell>} />
      <Route path="/wall-of-fame" element={<Shell><WallOfFame /></Shell>} />
      <Route path="/community" element={<ProtectedRoute><Shell><CommunityPage /></Shell></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><Shell><ChallengesPage /></Shell></ProtectedRoute>} />
      <Route path="/auth/callback" element={<Shell><AuthCallback /></Shell>} />

      <Route path="/dashboard" element={<ProtectedRoute><DashboardRedirect /></ProtectedRoute>} />

      <Route
        path="/dashboard/student"
        element={
          <ProtectedRoute roles={["student", "admin"]}>
            <Shell><StudentDashboard /></Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/teacher"
        element={
          <ProtectedRoute roles={["teacher", "admin"]}>
            <Shell><TeacherDashboard /></Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin"
        element={
          <ProtectedRoute roles={["admin"]}>
            <Shell><AdminDashboard /></Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/live/:classId"
        element={
          <ProtectedRoute>
            <Shell hideChrome><LiveRoom /></Shell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function DashboardRedirect() {
  const { user } = useAuth();
  if (!user) return null;
  const path = user.role === "admin" ? "/dashboard/admin" : user.role === "teacher" ? "/dashboard/teacher" : "/dashboard/student";
  return <Navigate to={path} replace />;
}

export default App;
